import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Shield, Award, Clock, CheckCircle, XCircle, ArrowRight, User } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a49fb855-3c1f-4bf2-8664-337bfce9e50d/artifacts/sokgx5c0_Gold.png";

// Custom Logo component with proper styling to remove black background effect
const Logo = ({ className = "", size = "h-24" }) => (
  <div className={`relative ${className}`}>
    <img 
      src={LOGO_URL} 
      alt="TC PPF" 
      className={`${size} w-auto object-contain`}
      style={{ 
        filter: 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.3))',
        mixBlendMode: 'lighten'
      }}
    />
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('certificate');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);
    
    try {
      let params = {};
      if (searchType === 'certificate') {
        if (!certificateNumber.trim()) {
          toast.error('Please enter a certificate number');
          setLoading(false);
          return;
        }
        params.certificate_number = certificateNumber.trim();
      } else {
        if (!firstName.trim() && !lastName.trim()) {
          toast.error('Please enter first name or last name');
          setLoading(false);
          return;
        }
        if (firstName.trim()) params.first_name = firstName.trim();
        if (lastName.trim()) params.last_name = lastName.trim();
      }
      
      const response = await axios.get(`${API}/public/search`, { params });
      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No certificates found matching your search');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <div className="h-12 w-12 flex items-center justify-center">
              <img 
                src={LOGO_URL} 
                alt="TC PPF" 
                className="h-12 w-auto object-contain"
                style={{ mixBlendMode: 'lighten' }}
              />
            </div>
          </Link>
          <Link to="/admin/login">
            <Button 
              variant="ghost" 
              className="text-zinc-400 hover:text-white hover:bg-white/5"
              data-testid="admin-login-link"
            >
              <User className="w-4 h-4 mr-2" />
              Admin Portal
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center mb-16 animate-fade-in">
          <div className="flex justify-center mb-8">
            <Logo size="h-28" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6">
            <span className="text-gold-gradient">Certificate</span>
            <br />
            <span className="text-zinc-300 font-light">Verification</span>
          </h1>
          <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto">
            Verify your TC Protection Film certification status instantly
          </p>
        </div>

        {/* Search Card */}
        <Card className="glass-card max-w-2xl mx-auto mb-16 animate-slide-up stagger-1" data-testid="search-card">
          <CardContent className="p-8">
            {/* Search Type Toggle */}
            <div className="flex gap-2 mb-6 p-1 bg-zinc-900/50 rounded-lg">
              <button
                onClick={() => setSearchType('certificate')}
                className={`flex-1 py-3 px-4 rounded-md text-sm font-semibold uppercase tracking-wider transition-all ${
                  searchType === 'certificate' 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="search-type-certificate"
              >
                Certificate Number
              </button>
              <button
                onClick={() => setSearchType('name')}
                className={`flex-1 py-3 px-4 rounded-md text-sm font-semibold uppercase tracking-wider transition-all ${
                  searchType === 'name' 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="search-type-name"
              >
                Name Search
              </button>
            </div>

            <form onSubmit={handleSearch}>
              {searchType === 'certificate' ? (
                <div className="mb-6">
                  <Input
                    type="text"
                    placeholder="Enter your certificate number..."
                    value={certificateNumber}
                    onChange={(e) => setCertificateNumber(e.target.value)}
                    className="h-14 text-lg bg-black/50 border-2 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-zinc-600"
                    data-testid="certificate-number-input"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <Input
                    type="text"
                    placeholder="First name..."
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="h-14 text-lg bg-black/50 border-2 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-zinc-600"
                    data-testid="first-name-input"
                  />
                  <Input
                    type="text"
                    placeholder="Last name..."
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="h-14 text-lg bg-black/50 border-2 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-zinc-600"
                    data-testid="last-name-input"
                  />
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full btn-gold h-14 text-lg rounded-lg"
                disabled={loading}
                data-testid="search-button"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full"></div>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Verify Certificate
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && (
          <div className="max-w-3xl mx-auto animate-fade-in">
            {results.length > 0 ? (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl text-white mb-6 text-center">Search Results</h2>
                {results.map((cert, index) => (
                  <Card 
                    key={cert.id} 
                    className={`glass-card hover:border-[#D4AF37]/30 transition-all cursor-pointer stagger-${index + 1}`}
                    onClick={() => navigate(`/certificate/${cert.certificate_number}`)}
                    data-testid={`certificate-result-${cert.certificate_number}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Award className="w-6 h-6 text-[#D4AF37]" />
                            <h3 className="font-heading text-xl text-white">
                              {cert.first_name} {cert.last_name}
                            </h3>
                          </div>
                          <p className="text-zinc-400 text-sm mb-3">
                            Certificate: <span className="text-[#D4AF37] font-mono">{cert.certificate_number}</span>
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Issued: {formatDate(cert.certification_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Expires: {formatDate(cert.expiration_date)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                            cert.is_active ? 'status-active' : 'status-expired'
                          }`}>
                            {cert.is_active ? (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Active
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <XCircle className="w-3 h-3" />
                                Expired
                              </span>
                            )}
                          </span>
                          <ArrowRight className="w-5 h-5 text-zinc-500" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <Shield className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-white mb-2">No Certificates Found</h3>
                  <p className="text-zinc-400">
                    No certifications match your search criteria. Please check the details and try again.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Features */}
        {!searched && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: Shield, title: 'Verified', desc: 'Authentic certification verification' },
              { icon: Award, title: 'Professional', desc: 'Industry-recognized credentials' },
              { icon: Clock, title: 'Real-time', desc: 'Instant status updates' }
            ].map((feature, i) => (
              <Card key={i} className="glass-card group hover:border-[#D4AF37]/20 transition-all animate-slide-up" style={{ animationDelay: `${0.2 + i * 0.1}s` }}>
                <CardContent className="p-6 text-center">
                  <feature.icon className="w-10 h-10 text-[#D4AF37] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="font-heading text-lg text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-zinc-400">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-zinc-500 text-sm">
            &copy; {new Date().getFullYear()} TC Protection Film. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
